import React, { useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomCategorySelectSingle from "../CategorySelect/CustomCategorySelectSingle";
import CustomMiddleSelect from "../../Select/CustomMiddleSelect";

/**
 * CustomCascadeSelect (汎用カスケード選択)
 *
 * cascading-form-dropdowns プロトの UI/UX を MUI + 共有部品で再現した汎用コンポーネント。
 * - 順引き: 上位 select を選ぶと下位が出現／上位変更で下位リセット。末端 select 後に
 *   auto 列(テナント名 等)を「自動表示」バッジ付き読み取り専用で表示。
 * - 逆引き(reverse 指定時): モード切替トグルを出し、reverse.key 列を検索 select に。
 *   選ぶと一致行から他列を自動解決して表示。
 *
 * データ駆動: rows は「全 key の値を持つ 1 経路 = 末端アイテム」の配列。
 * 表示名は `<key>_label` を持てばそれを使い、無ければ value をそのまま表示する。
 */

export interface CascadeColumn {
  /** rows 内のフィールドキー */
  key: string;
  label: string;
  /** select=ドロップダウン / auto=自動表示(読み取り専用) */
  role: "select" | "auto";
  /** select: 検索入力を出すか (選択肢が少なければ false)。既定 true */
  searchable?: boolean;
  placeholder?: string;
  /** auto: 上位未確定時のヒント (例「名称を選択すると自動で表示されます」) */
  hint?: string;
  /** auto: 一致行はあるが値が無い時 (例「テナント登録なし」) */
  emptyText?: string;
  /** select: 固定の選択肢。未指定なら rows から上位選択で絞って導出する。 */
  options?: { value: string; label: string }[];
}

export interface CustomCascadeSelectProps {
  /** 順引きの並び (例: 収益カテゴリ→場所区分→名称→テナント名) */
  columns: CascadeColumn[];
  /** 各行 = 完全な 1 経路 (全 key の値。表示名は `<key>_label`)。 */
  rows: Array<Record<string, string | null>>;
  /** 選択中の値 (key→value)。select 列のみ保持、auto 列は導出。 */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** 指定すると逆引きモードを有効化。key = 検索対象 (通常は末端 auto 列)。 */
  reverse?: {
    key: string;
    forwardLabel?: string;
    reverseLabel?: string;
    searchPlaceholder?: string;
    /** 検索一致無し時の文言 (未指定なら select 既定)。ドメイン固有文言は呼び出し側で渡す。 */
    emptyText?: string;
  };
  labelWidth?: string;
  inputWidth?: string;
  /** true で全体を角丸・薄グレー背景の枠で囲う (プロト準拠)。 */
  boxed?: boolean;
  /** 指定すると枠の上に見出しラベルを表示 (例「場所・テナント選択」)。 */
  title?: string;
  /**
   * true でモード (場所から探す ⇄ テナントから探す) ごとに独立した下書きを保持する。
   * 探している最中は両モードに別々の選択を持て、切替時はミラーせず各モードの
   * 最後の状態を復元する (未操作のモードは空)。確定値 = アクティブなモードの選択。
   * 既定 (false) は従来どおり切替で選択をリセットする。
   */
  independentModeDrafts?: boolean;
}

const AUTO_BADGE_SX = {
  borderRadius: "4px",
  bgcolor: "#EFEFEF",
  px: 0.75,
  py: 0.25,
  fontSize: 11,
  fontWeight: 600,
  color: "#989898",
  lineHeight: 1,
} as const;

const labelOf = (
  row: Record<string, string | null>,
  key: string,
): string => {
  const lbl = row[`${key}_label`];
  if (typeof lbl === "string" && lbl) return lbl;
  const v = row[key];
  return typeof v === "string" ? v : "";
};

const CustomCascadeSelect: React.FC<CustomCascadeSelectProps> = ({
  columns,
  rows,
  value,
  onChange,
  reverse,
  labelWidth = "130px",
  inputWidth = "100%",
  boxed = false,
  title,
  independentModeDrafts = false,
}) => {
  const [mode, setMode] = useState<"forward" | "reverse">("forward");
  // independentModeDrafts 用: モードごとの最後の選択を退避しておく。
  // 切替時に現モードの value を退避し、次モードの下書きを復元する。
  const modeDraftsRef = useRef<{
    forward?: Record<string, string>;
    reverse?: Record<string, string>;
  }>({});

  const selectCols = useMemo(
    () => columns.filter((c) => c.role === "select"),
    [columns],
  );
  const autoCols = useMemo(
    () => columns.filter((c) => c.role === "auto"),
    [columns],
  );
  const leafKey = selectCols[selectCols.length - 1]?.key;

  // 選択中の select 値で一致する末端行 (auto 列の解決に使う)
  const matchedRow = useMemo(() => {
    if (selectCols.some((c) => !value[c.key])) return null;
    return (
      rows.find((r) => selectCols.every((c) => r[c.key] === value[c.key])) ??
      null
    );
  }, [rows, selectCols, value]);

  // ── 順引き: select 列のオプション (上位の選択で絞り込み) ──
  const optionsForSelect = (colIdx: number): { value: string; label: string }[] => {
    const col = selectCols[colIdx];
    if (col.options) return col.options; // 固定オプション指定時はそのまま
    const prior = selectCols.slice(0, colIdx);
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (prior.some((p) => r[p.key] !== value[p.key])) continue;
      const v = r[col.key];
      if (typeof v !== "string" || !v) continue;
      if (!seen.has(v)) seen.set(v, labelOf(r, col.key));
    }
    return Array.from(seen, ([v, label]) => ({ value: v, label }));
  };

  // 先頭 select はカスケードの入口なので常に表示する。
  // データ(rows)が無くても空セレクト + emptyText を出し、逆引き(テナント側)の
  // 検索 select が常に出るのと挙動を揃える。
  // 2 列目以降は「上位がすべて選ばれ、かつこの列に選択肢がある」時のみ表示
  // (例: その他カテゴリ→場所区分の候補が無い → 場所区分以降を出さない)。
  const isSelectVisible = (colIdx: number): boolean =>
    colIdx === 0 ||
    (selectCols.slice(0, colIdx).every((p) => Boolean(value[p.key])) &&
      optionsForSelect(colIdx).length > 0);

  const handleSelect = (colIdx: number, v: string) => {
    const col = selectCols[colIdx];
    const next: Record<string, string> = { ...value, [col.key]: v };
    // 下位 select はリセット
    for (const c of selectCols.slice(colIdx + 1)) delete next[c.key];
    onChange(next);
  };

  // ── 逆引き: reverse.key の検索オプション (値ありの行から distinct) ──
  const reverseOptions = useMemo(() => {
    if (!reverse) return [];
    const seen = new Map<string, string>();
    for (const r of rows) {
      const v = r[reverse.key];
      if (typeof v !== "string" || !v) continue;
      if (!seen.has(v)) seen.set(v, labelOf(r, reverse.key));
    }
    return Array.from(seen, ([v, label]) => ({ value: v, label }));
  }, [reverse, rows]);

  // 逆引きの選択値。value に reverse.key を持たない場合は、選択中の末端(leaf)から導出
  // (親が category/area/name しか保持しなくても検索欄の選択が保てる)。
  const reverseSelected = useMemo(() => {
    if (!reverse) return "";
    if (value[reverse.key]) return value[reverse.key];
    if (leafKey && value[leafKey]) {
      const r = rows.find((row) => row[leafKey] === value[leafKey]);
      const tv = r?.[reverse.key];
      if (typeof tv === "string") return tv;
    }
    return "";
  }, [reverse, value, leafKey, rows]);
  const reverseRow = useMemo(() => {
    if (!reverse || !reverseSelected) return null;
    return rows.find((r) => r[reverse.key] === reverseSelected) ?? null;
  }, [reverse, reverseSelected, rows]);

  const handleReverseSelect = (v: string) => {
    if (!reverse) return;
    if (!v) {
      onChange({});
      return;
    }
    const row = rows.find((r) => r[reverse.key] === v);
    if (!row) {
      onChange({ [reverse.key]: v });
      return;
    }
    // 一致行から全 select 列 + reverse.key を確定
    const next: Record<string, string> = { [reverse.key]: v };
    for (const c of selectCols) {
      const cv = row[c.key];
      if (typeof cv === "string") next[c.key] = cv;
    }
    onChange(next);
  };

  const switchMode = (next: "forward" | "reverse") => {
    if (next === mode) return;
    if (independentModeDrafts) {
      // 現モードの選択を退避 → 次モードの下書きを復元 (未操作なら空=新規で探せる)。
      // 確定値は常にアクティブモードの選択 = 復元した value。登録は単一。
      modeDraftsRef.current[mode] = value;
      setMode(next);
      onChange(modeDraftsRef.current[next] ?? {});
      return;
    }
    setMode(next);
    onChange({}); // 既定: モード切替で選択リセット
  };

  // ── auto 列の表示要素 ──
  const renderAuto = (
    col: CascadeColumn,
    sourceRow: Record<string, string | null> | null,
    leafSelected: boolean,
  ) => {
    let body: React.ReactNode;
    if (!leafSelected || !sourceRow) {
      body = (
        <Typography sx={{ fontSize: 12, color: "#989898" }}>
          {col.hint ?? "-"}
        </Typography>
      );
    } else {
      const v = sourceRow[col.key];
      body =
        typeof v === "string" && v ? (
          <Typography sx={{ fontSize: 12, color: "#3E3E3E" }}>
            {labelOf(sourceRow, col.key)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: "#989898" }}>
            {col.emptyText ?? ""}
          </Typography>
        );
    }
    return (
      <Box
        key={col.key}
        sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        {/* 1行目: ラベル + 自動表示バッジ。色/太さは select ラベルと同じ (#3E3E3E / 700)。 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            component="span"
            sx={{ fontSize: 12, fontWeight: 700, color: "#3E3E3E" }}
          >
            {col.label}
          </Box>
          <Box component="span" sx={AUTO_BADGE_SX}>
            自動表示
          </Box>
        </Box>
        {/* 2行目: 値。ラベルより内容を少し右にインデントする。 */}
        <Box
          sx={{ minHeight: 24, display: "flex", alignItems: "center", pl: 1 }}
        >
          {body}
        </Box>
      </Box>
    );
  };

  // 順引き (場所から): select 列 → 末端後に auto 列
  const forwardContent = (
    <>
      {selectCols.map((col, i) =>
        isSelectVisible(i) ? (
          <Box key={col.key} sx={{ "& > *": { margin: "0 !important" } }}>
            <CustomCategorySelectSingle
              label={col.label}
              name={`cascade_${col.key}`}
              options={optionsForSelect(i)}
              selectedValue={value[col.key] ?? ""}
              onValueChange={(v) => handleSelect(i, v)}
              searchable={col.searchable ?? true}
              clearable={false}
              placeholder={col.placeholder ?? ""}
              emptyText={col.emptyText}
              labelWidth={labelWidth}
              inputWidth={inputWidth}
            />
          </Box>
        ) : null,
      )}
      {/* auto 列は末端 select が表示可能(=カスケードが到達)になってから表示 */}
      {isSelectVisible(selectCols.length - 1) &&
        autoCols.map((col) =>
          renderAuto(col, matchedRow, Boolean(value[leafKey])),
        )}
    </>
  );

  // 逆引き (テナントから): reverse.key を検索 select に + 残り列を自動表示
  const reverseColLabel =
    columns.find((c) => c.key === reverse?.key)?.label ?? "";
  const reverseContent = reverse ? (
    <>
      <Box sx={{ "& > *": { margin: "0 !important" } }}>
        <CustomCategorySelectSingle
          label={reverseColLabel}
          name={`cascade_reverse_${reverse.key}`}
          options={reverseOptions}
          selectedValue={reverseSelected}
          onValueChange={handleReverseSelect}
          searchable
          clearable={false}
          // 閉じた状態のプレースホルダー。列ラベルから「○○を選択」(例: テナント名を選択)。
          placeholder={`${reverseColLabel}を選択`}
          emptyText={reverse.emptyText}
          labelWidth={labelWidth}
          inputWidth={inputWidth}
        />
      </Box>
      {/* 逆引きも順引き同様、検索対象 (テナント等) を選ぶまで自動表示行は出さない。
          初期は検索 select 1 個だけ表示する。 */}
      {reverseSelected &&
        selectCols.map((col) =>
          renderAuto(col, reverseRow, Boolean(reverseSelected)),
        )}
    </>
  ) : null;

  const content = (
    <Stack
      spacing={2}
      sx={
        boxed
          ? {
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              bgcolor: "#FAFAFA",
              p: 1,
            }
          : undefined
      }
    >
      {reverse && (
        // モード切替は書類設定の「個人/法人」と同じ CustomMiddleSelect (セグメント)。
        // 全体を少し左へ寄せる (セグメント内側パディングぶんの見え方を補正)。
        <Box sx={{ pr: "8px" }}>
          <CustomMiddleSelect
            options={[
              { value: "forward", label: reverse.forwardLabel ?? "" },
              { value: "reverse", label: reverse.reverseLabel ?? "" },
            ]}
            value={mode}
            onChange={(val) =>
              switchMode(val === "reverse" ? "reverse" : "forward")
            }
            width="100%"
            marginBottom={0}
          />
        </Box>
      )}
      {mode === "forward" || !reverse ? forwardContent : reverseContent}
    </Stack>
  );

  return title ? (
    <Box>
      <Typography
        sx={{ fontSize: 12, fontWeight: 700, color: "#3E3E3E", mb: 0.5 }}
      >
        {title}
      </Typography>
      {content}
    </Box>
  ) : (
    content
  );
};

export default CustomCascadeSelect;
